import React, { useCallback } from "react";
import { Box } from "@mui/material";
import { useNavigationConfirm } from "./hooks/useNavigationConfirm";
import CustomModal from "../Modal/CustomModal";
import CustomButton from "../Buttons/Button/CustomButton";

export interface CustomFormNavigationGuardProps {
  /** フォームが変更されているかどうか */
  hasFormChanged: boolean;
  /** 送信中かどうか（追加のブロック条件） */
  isSubmitting?: boolean;
  /** 確認モーダルのメッセージ（関数も可能） */
  message?: string | ((isProcessing: boolean) => string);
  /** 確認ボタンのラベル（関数も可能） */
  confirmLabel?: string | ((isProcessing: boolean) => string);
  /** キャンセルボタンのラベル */
  cancelLabel?: string;
  /** モーダルのタイトル（関数も可能） */
  title?: string | ((isProcessing: boolean) => string);
  /** 追加の条件（例：処理中、ファイルアップロード中など） */
  additionalBlockConditions?: boolean;
  /**
   * 子コンポーネントをレンダリングするためのrender prop
   * 
   * 注意: CustomFormNavigationGuardは自動的にナビゲーションをインターセプトします。
   * カスタムナビゲーションが必要な場合は、children内でnavigateWithConfirmやnavigateWithoutConfirmを使用できます。
   */
  children?: () => React.ReactNode;
  /** 確認モーダルで「移動」をクリックした時の追加処理 */
  onConfirm?: () => void;
  /** タブ切り替え時の確認を無効にするかどうか */
  ignoreTabNavigation?: boolean;
}

const CustomFormNavigationGuard: React.FC<CustomFormNavigationGuardProps> = ({
  hasFormChanged,
  isSubmitting = false,
  message = "入力中の情報が失われますが、よろしいでしょうか？",
  confirmLabel = "移動",
  cancelLabel = "戻る",
  title = "ページ移動の確認",
  additionalBlockConditions = false,
  children,
  onConfirm,
  ignoreTabNavigation = false,
}) => {
  const [temporarilyAllowNavigation, setTemporarilyAllowNavigation] =
    React.useState(false);

  const shouldBlockNavigation =
    (hasFormChanged || isSubmitting || additionalBlockConditions) &&
    !temporarilyAllowNavigation;

  const isProcessing = isSubmitting || additionalBlockConditions;

  const resolvedMessage =
    typeof message === "function" ? message(isProcessing) : message;
  const resolvedTitle =
    typeof title === "function" ? title(isProcessing) : title;
  const resolvedConfirmLabel =
    typeof confirmLabel === "function"
      ? confirmLabel(isProcessing)
      : confirmLabel;

  const {
    showModal,
    confirmNavigation: originalConfirmNavigation,
    cancelNavigation,
    modalMessage,
  } = useNavigationConfirm({
    shouldBlock: shouldBlockNavigation,
    message: resolvedMessage,
    ignoreTabNavigation,
  });

  const confirmNavigation = useCallback(() => {
    setTemporarilyAllowNavigation(true);
    if (onConfirm) {
      onConfirm();
    }
    setTimeout(() => {
      originalConfirmNavigation();
      setTimeout(() => {
        setTemporarilyAllowNavigation(false);
      }, 1000);
    }, 50);
  }, [originalConfirmNavigation, onConfirm]);

  return (
    <>
      {children?.()}

      {/* ナビゲーション離脱確認モーダル。modalType="deleteModal" は CustomModal で本文(bodyText)を表示するために指定 */}
      <CustomModal
        openModal={showModal}
        handleCloseModal={cancelNavigation}
        title={resolvedTitle}
        bodyText={modalMessage}
        modalType="deleteModal"
        bodyTextAlign="left"
        sx={{ padding: "0px !important"}}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "10px"
          }}
        >
          <CustomButton
            label={cancelLabel}
            buttonCategory="cancelModal"
            onClick={cancelNavigation}
            sx={{
              width: "50% !important",
            }}
          />
          <CustomButton
            label={resolvedConfirmLabel}
            buttonCategory="confirm"
            onClick={confirmNavigation}
            sx={{
              width: "50% !important",
            }}
          />
        </Box>
      </CustomModal>
    </>
  );
};

export default CustomFormNavigationGuard;

